// ============================================================================
// AiCompletionRibbon
// ----------------------------------------------------------------------------
// Persistent ribbon that appears when AI processing finishes while the user is
// looking at another page — an in-flow "your quote is ready" (or error) banner
// with a VIEW RESULT deep-link and a dismiss control. Construction-safety-tape
// visual DNA (diagonal hatching, ink-black + safety-orange).
//
// TS-060 / DEC-COUNCIL-015 T3: extracted VERBATIM from App.jsx's render
// (behaviour-preserving; only type annotations + this header added). App keeps
// the `ribbonNotification &&` visibility gate and owns the navigation/stage-
// transition logic behind VIEW RESULT (passed as onViewResult) plus the
// dismiss handler — no pipeline state moves into this presentational leaf.
// ============================================================================

import React from 'react';
import { Icons } from './Icon';

interface AiCompletionRibbonProps {
    /** The active notification. `type` includes 'error' for the error variant. */
    notification: { type: string; message: string };
    /** True while the exit animation is playing. */
    dismissing: boolean;
    /** Deep-links to the finished result (App owns the nav/stage logic). */
    onViewResult: () => void;
    /** Dismisses the ribbon. */
    onDismiss: () => void;
}

export function AiCompletionRibbon({ notification, dismissing, onViewResult, onDismiss }: AiCompletionRibbonProps) {
    return (
        <div
            className={`ribbon-notification ${dismissing ? 'ribbon-exit' : 'ribbon-enter'}`}
            style={{
                background: notification.type.includes('error')
                    ? 'linear-gradient(90deg, #1a1a1a 0%, #2d1a1a 50%, #1a1a1a 100%)'
                    : 'linear-gradient(90deg, #1a1a1a 0%, #2d1f15 50%, #1a1a1a 100%)',
                borderBottom: `2px solid ${notification.type.includes('error') ? '#ef4444' : 'var(--qm-accent)'}`,
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                overflow: 'hidden',
                position: 'relative',
                zIndex: 40,
                flexShrink: 0,
            }}
        >
            {/* Diagonal hatching (construction safety tape DNA) */}
            <div style={{ position: 'absolute', inset: 0, opacity: 0.03, pointerEvents: 'none',
                background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 12px)' }} />
            {notification.type.includes('error')
                ? <Icons.AlertTriangle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                : <Icons.CheckCircle size={16} style={{ color: 'var(--qm-accent)', flexShrink: 0 }} />
            }
            <span style={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {notification.message}
            </span>
            {!notification.type.includes('error') && (
                <button
                    onClick={onViewResult}
                    style={{ background: 'var(--qm-accent)', color: 'var(--qm-accent-on)', border: '2px solid var(--qm-accent-700)', borderRadius: '6px', padding: '4px 14px',
                        fontSize: '11px', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    VIEW RESULT
                </button>
            )}
            <button onClick={onDismiss}
                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '4px', width: '24px', height: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'rgba(255,255,255,0.5)' }}>
                <Icons.X size={14} />
            </button>
        </div>
    );
}
